/**
 * Variant Row Actions — overflow menu with Archive/Delete/Restore for variant rows.
 * Extracted from ItemVariationsTabWidget to keep file size manageable.
 * Reuses the smart delete flow from Epic 02 (Plan 174).
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { erp } from '@/lib/erp';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { buildDocRoute } from '@/lib/config/docRoutes';
import { useSmartDeleteFlow } from '@/features/common/api/useSmartDeleteFlow';
import { useRestoreDoc } from '@/features/common/api/useRestoreDoc';
import {
  SmartArchiveDialog,
  ChoiceDialog,
  PermanentDeleteConfirm,
} from '@/features/common/components/archive-delete';

interface VariantRowActionsProps {
  variantName: string;
  itemCode: string;
  disabled: 0 | 1 | undefined;
  templateItemCode: string;
  showArchivedVariants: boolean;
}

/**
 * Overflow menu (•••) for a single variant row.
 * Provides: View, Archive/Delete (via smart flow), Restore (for archived).
 *
 * IMPORTANT: Does NOT use useDeleteItem — that hook hardcodes navigate(routes.item.list).
 * Instead uses an inline mutation with erp.items.delete().
 */
export function VariantRowActions({
  variantName,
  itemCode,
  disabled,
  templateItemCode,
  showArchivedVariants,
}: VariantRowActionsProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const variantRoute = buildDocRoute('Item', itemCode);
  const isArchived = disabled === 1;

  // Smart delete flow — reuses Epic 02 infrastructure
  const {
    dialogState,
    setDialogState,
    safetyData,
    initiateDelete,
    closeDialogs,
    archiveMutation,
    isChecking,
  } = useSmartDeleteFlow('Item', variantName);

  // Restore mutation for archived variants
  const restoreMutation = useRestoreDoc('Item');

  // Permanent delete — inline mutation, does NOT navigate away
  const permanentDeleteMutation = useMutation<void, Error, void>({
    mutationFn: () => erp.items.delete(variantName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', variantName] });
      queryClient.invalidateQueries({ queryKey: ['items', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['template-variants-summary', templateItemCode] });
      queryClient.invalidateQueries({ queryKey: ['archived-count'] });
      toast.success('Variant permanently deleted');
      closeDialogs();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete variant');
    },
  });

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    initiateDelete();
  }, [initiateDelete]);

  const handleRestore = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    restoreMutation.mutate(
      { name: variantName },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['template-variants-summary', templateItemCode] });
        },
      },
    );
  }, [restoreMutation, variantName, queryClient, templateItemCode]);

  const handleArchiveSuccess = useCallback(() => {
    archiveMutation.mutate(
      { name: variantName },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['template-variants-summary', templateItemCode] });
          closeDialogs();
        },
      },
    );
  }, [archiveMutation, variantName, queryClient, templateItemCode, closeDialogs]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => e.stopPropagation()}
            className="h-8 w-8 p-0"
            aria-label={`Actions for ${itemCode}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(variantRoute); }}>
            View variant
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isArchived && showArchivedVariants && (
            <DropdownMenuItem onClick={handleRestore} disabled={restoreMutation.isPending}>
              Restore
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={handleDelete}
            disabled={isChecking}
            className="text-destructive focus:text-destructive"
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Smart delete dialogs */}
      <SmartArchiveDialog
        open={dialogState === 'smart-archive'}
        onOpenChange={(open) => !open && closeDialogs()}
        itemName={itemCode}
        linkedSummary={safetyData?.linked_summary || ''}
        onArchive={handleArchiveSuccess}
        isArchiving={archiveMutation.isPending}
      />

      <ChoiceDialog
        open={dialogState === 'choice'}
        onOpenChange={(open) => !open && closeDialogs()}
        itemName={itemCode}
        hasDisabledField={safetyData?.has_disabled_field ?? true}
        onArchive={handleArchiveSuccess}
        onPermanentDelete={() => setDialogState('permanent-delete')}
        isArchiving={archiveMutation.isPending}
      />

      <PermanentDeleteConfirm
        open={dialogState === 'permanent-delete'}
        onOpenChange={(open) => !open && closeDialogs()}
        itemName={itemCode}
        doctype="Item"
        onConfirm={() => permanentDeleteMutation.mutate()}
        isDeleting={permanentDeleteMutation.isPending}
      />
    </>
  );
}
